import { RateLimitError, TimeoutError, NetworkError } from '../types/errors.js';
import { getRetryDelay, sleep } from './utils.js';

/** Determines if an error is retryable */
function isRetryable(error: unknown): boolean {
  if (error instanceof RateLimitError) {
    return true;
  }
  if (error instanceof TimeoutError) {
    return true;
  }
  if (error instanceof NetworkError) {
    return true;
  }
  // Retry on 408 Request Timeout and 5xx server errors
  if (error instanceof Error && 'status' in error) {
    const status = (error as { status: number }).status;
    return status === 408 || (status >= 500 && status < 600);
  }
  return false;
}

/** Execute a function with exponential backoff retry */
export async function withRetry<T>(fn: () => Promise<T>, maxRetries: number): Promise<T> {
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      if (attempt >= maxRetries || !isRetryable(error)) {
        throw error;
      }

      const delay = getRetryDelay(attempt);

      // Respect Retry-After header if available
      if (error instanceof RateLimitError && error.retryAfter !== undefined) {
        await sleep(error.retryAfter);
      } else {
        await sleep(delay);
      }
    }
  }

  // Unreachable — the loop always returns or throws
  throw new Error('withRetry: unexpected exit');
}
